<template>
	<v-container>
		<v-card outlined>
			<v-card-title>
				<span>Заявки на ремонт</span>
				<v-spacer />
				<v-btn
					outlined
					small
					to="/orders/repair/create"
				>
					Создать заявку
				</v-btn>
			</v-card-title>
			<v-card-text class="pb-0">
				<v-alert
					v-model="showHelp"
					border="left"
					type="warning"
					class="mb-0"
					outlined
					dismissible
					@input="disableHelp"
				>
					Для открытия заявки нажмите на строку таблице. Для открытия в новой вкладке воспользуйтесь ссылкой в столбце ID
				</v-alert>
			</v-card-text>
			<v-card-text>
				<v-form ref="form" @submit.stop.prevent="submitFilters">
					<v-row>
						<v-col cols="12" sm="6" md="4" lg="2">
							<v-select
								:value="$route.query.state"
								name="state"
								:items="orders.meta.states"
								label="Статус"
								item-value="name"
								item-text="display_name"
								outlined
								dense
								clearable
								hide-details
							/>
						</v-col>
						<v-col cols="0" md="4" lg="0" class="pa-0 d-none d-md-flex d-lg-none" />
						<v-col cols="12" sm="6" md="6" lg="3">
							<v-autocomplete
								:value="$route.query.client"
								v-model="clientID"
								:search-input.sync="autocompletes.client.term"
								:items="autocompletes.client.items"
								label="Контрагент"
								name="client"
								item-text="name"
								item-value="id"
								autocomplete="off"
								outlined
								dense
								clearable
								hide-details
								@change="searchPlaces('')"
							/>
						</v-col>
						<v-col cols="12" sm="6" md="6" lg="3">
							<v-autocomplete
								:value="$route.query.place"
								:search-input.sync="autocompletes.place.term"
								:items="autocompletes.place.items"
								:disabled="!clientID"
								:label="`Объект ${!clientID ? '(выберите контрагента)' : ''}`"
								name="place"
								item-text="name"
								item-value="id"
								autocomplete="off"
								outlined
								dense
								clearable
								hide-details
							/>
						</v-col>
						<v-col cols="12" sm="6" md="6" lg="3">
							<v-autocomplete
								:value="$route.query.mechanic"
								:search-input.sync="autocompletes.mechanic.term"
								:items="autocompletes.mechanic.items"
								label="Механик"
								name="mechanic"
								item-text="name"
								item-value="id"
								autocomplete="off"
								outlined
								dense
								clearable
								hide-details
							/>
						</v-col>

						<v-col cols="12" sm="6" md="6" lg="2">
							<v-text-field
								:value="$route.query['created:from']"
								name="created:from"
								type="date"
								label="Создана с"
								outlined
								dense
								clearable
								hide-details
							/>
						</v-col>

						<v-col cols="12" sm="6" md="6" lg="2">
							<v-text-field
								:value="$route.query['created:to']"
								name="created:to"
								type="date"
								label="Создана по"
								outlined
								dense
								clearable
								hide-details
							/>
						</v-col>


						<v-col cols="12" sm="6" md="4" lg="4">
							<v-btn
								type="submit"
								class="mr-2"
								outlined
							>
								Применить
							</v-btn>
							<v-btn
								color="grey"
								class="mr-2"
								outlined
								@click.stop="clearFilters"
							>
								Сбросить
							</v-btn>
							<v-btn
								color="primary"
								outlined
								:loading="loadingExport"
								@click.stop="exportOrders"
							>
								Экспорт
							</v-btn>
						</v-col>
					</v-row>
				</v-form>
			</v-card-text>
			<v-data-table
				:headers="headers"
				:items="orders.data"
				:server-items-length="orders.meta.total"
				:loading="loading"
				loading-text="Загрузка..."
				disable-sort
				@update:page="fetchRepairOrders($event, orders.meta.per_page, getFilters())"
				@update:items-per-page="fetchRepairOrders(1, $event, getFilters())"
				@click:row="$router.push(`/orders/repair/${$event.id}`)"
			>
				<template #item.id="{ item: order }">
					<router-link
						:to="`/orders/repair/${order.id}`"
						v-text="order.id"
					/>
				</template>
				<template #item.state="{ item: order }">
					<v-chip
						color="blue"
						label
						small
						outlined
					>
						{{ order.state.display_name }}
					</v-chip>
				</template>
				<template #item.city="{ item: order }">
					<p class="mb-0">{{ order.place.name }}</p>
					<p class="mb-0 caption">
						{{ order.place.city.name }}, {{ order.place.address }}
					</p>
				</template>
				<template #item.machine="{ item: order }">
					<p class="mb-0">{{ order.machine.name }}</p>
					<p class="mb-0 caption">
						{{ order.machine.type }}
					</p>
				</template>
				<template #item.client="{ item: order }">
					<router-link
						:to="`/clients/${order.place.client.id}`"
						@click.stop.prevent="$router.push(`/clients/${order.place.client.id}`)"
					>
						{{ order.place.client.name }}
					</router-link>
				</template>
				<template #item.mechanic="{ item: order }">
					<router-link
						v-if="order.mechanic !== null"
						:to="`/users/${order.mechanic.id}`"
					>
						{{ order.mechanic.name }}
					</router-link>
					<p v-else class="mb-0">Не назначен</p>
				</template>
				<template #item.tags="{ item: order }">
					<v-chip
						v-for="tag in order.tags"
						:key="tag.id"
						:color="tag.color"
						class="mr-1"
						outlined
						small
					>
						{{ tag.display_name }}
					</v-chip>
				</template>
			</v-data-table>
		</v-card>
	</v-container>
</template>

<script>
import axios from 'axios';
import { FileDownloader } from '@/utils/file-downloader';
import qs from 'qs';

const HEADERS = [
	{ text: 'ID', value: 'id' },
	{ text: 'Статус', value: 'state' },
	{ text: 'Объект', value: 'city' },
	{ text: 'Оборудование', value: 'machine' },
	{ text: 'Контрагент', value: 'client' },
	{ text: 'Механик', value: 'mechanic' },
	{ text: 'Теги', value: 'tags' },
];

const DEFAULT_LIMIT = 10;

const HELP_COOKIE = 'help.repair-orders.index';

export default {
	name: 'RepairOrdersIndex',
	data: () => ({
		showHelp: true,
		loading: false,
		loadingExport: false,
		orders: {
			data: [],
			meta: {
				total: 0,
				per_page: DEFAULT_LIMIT,
				current_page: 1,
				states: [],
			},
		},
		autocompletes: {
			client: {
				term: '',
				items: [],
			},
			place: {
				term: '',
				items: [],
			},
			mechanic: {
				term: '',
				items: [],
			},
		},
		clientID: null,
	}),
	computed: {
		headers() {
			return HEADERS;
		},
	},
	watch: {
		'autocompletes.client.term'(val) {
			this.searchClients(val);
		},
		'autocompletes.place.term'(val) {
			this.searchPlaces(val);
		},
		'autocompletes.mechanic.term'(val) {
			this.searchMechanics(val);
		},
	},
	async created() {
		this.showHelp = this.$cookies.get(HELP_COOKIE) !== 'false';
		this.CancelToken = axios.CancelToken;
		this.sourceClients = this.CancelToken.source();
		this.sourcePlaces = this.CancelToken.source();
		this.sourceMechanics = this.CancelToken.source();
		await this.searchClients('');
		await this.searchPlaces('');
		await this.searchMechanics('');
	},
	async mounted() {
		await this.fetchRepairOrders(1, DEFAULT_LIMIT, this.getFilters());
	},
	methods: {
		async fetchRepairOrders(page = 1, limit = null, filter = {}) {
			if (this.loading) {
				return;
			}
			this.loading = true;
			limit = limit ? limit : this.orders.meta.per_page;
			try {
				const response = await this.$http.get('/api/v2/service/orders/repair', {
					params: {
						page,
						limit,
						filter,
						XDEBUG_SESSION_START: 1
					},
				});

				this.orders = response.data;
				await this.setFiltersToURL(filter);
			} catch (e) {
				this.$notify.error(e.message);
			} finally {
				this.loading = false;
			}
		},
		async exportOrders() {
			if (this.loadingExport) {
				return;
			}
			this.loadingExport = true;
			try {
				const response = await this.$http.get('/api/v2/service/orders/repair/export', {
					params: {
						filter: this.getFilters(),
					},
					responseType: 'arraybuffer',
				});
				const fileDownloader = new FileDownloader(
					response.data,
					response.headers['content-type'],
					response.headers['content-disposition'].replace(/(.*)filename=("*)([^"]*)("*)/g, '$3'),
				);
				fileDownloader.execute();
			} catch (e) {
				if (!axios.isCancel(e)) {
					this.$notify.error(e.message);
				}
			} finally {
				this.loadingExport = false;
			}
		},
		getFilters() {
			const initial = Object.fromEntries((new FormData(this.$refs.form.$el).entries()));
			return Object.keys(initial)
				.reduce((acc, key) => {
					if (!!initial[key]) {
						acc[key] = initial[key];
					}
					return acc;
				}, {});
		},
		submitFilters() {
			this.fetchRepairOrders(1, DEFAULT_LIMIT, this.getFilters());
		},
		clearFilters() {
			this.$refs.form.reset();
		},
		async setFiltersToURL(filters) {
			const query = qs.stringify(filters, {
				allowDots: true,
				skipNulls: true,
				encodeValuesOnly: true,
			});
			try {
				if (query.length) {
					await this.$router.push(`${this.$route.path}?${query}`)
				} else {
					await this.$router.push(this.$route.path);
				}
			} catch (e) {
				console.error(e)
			}
		},
		async searchClients(term) {
			try {
				this.sourceClients.cancel('Operation cancelled by user');
				this.sourceClients = this.CancelToken.source();
				const response = await this.$http.get('/api/v1/clients', {
					params: {
						limit: 50,
						filter: {
							name: term,
						},
					},
					cancelToken: this.sourceClients.token,
				});
				this.autocompletes.client.items = response.data.data;
			} catch (e) {
				if (!axios.isCancel(e)) {
					console.error(e);
				}
			}
		},
		async searchPlaces(term) {
			if (!this.clientID) return;
			try {
				this.sourcePlaces.cancel('Operation cancelled by user');
				this.sourcePlaces = this.CancelToken.source();
				const response = await this.$http.get(`/api/v2/service/clients/${this.clientID}/places`, {
					params: {
						limit: 50,
						filter: {
							search: term,
						},
					},
					cancelToken: this.sourcePlaces.token,
				});
				this.autocompletes.place.items = response.data.data;
			} catch (e) {
				if (!axios.isCancel(e)) {
					console.error(e);
				}
			}
		},
		async searchMechanics(term) {
			try {
				this.sourceMechanics.cancel('Operation cancelled by user');
				this.sourceMechanics = this.CancelToken.source();
				const response = await this.$http.get(`/api/v2/users`, {
					params: {
						limit: 50,
						filter: {
							role: [ 'mechanic', 'mechanics-chief' ],
							search: term,
						},
					},
					cancelToken: this.sourceMechanics.token,
				});
				this.autocompletes.mechanic.items = response.data.data;
			} catch (e) {
				if (!axios.isCancel(e)) {
					console.error(e);
				}
			}
		},
		disableHelp() {
			console.debug('setting');
			this.$cookies.set(HELP_COOKIE, 'false');
		},
	},
};
</script>

<style scoped>

</style>
